import { API_VERSION, CHARACTER_MODERATION_STATUS, CharacterType, ICharacter, useTranslation, useUserInfo } from "@next-platform/client-common-core";
import { CharacterCard, CharacterCardActions, CharacterCardVariant, IconVariants, Text, TextVariants } from "@next-platform/client-common-spicy-ui";
import { Spinner } from "@nextui-org/react";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import NavigateBackButton from "@/components/common/NavigateBackButton";
import PageStaticMetaTags from "@/components/common/PageStaticMetaTags";
import Trans from "@/components/Trans/Trans";
import useApi from "@/components/useApi";
import { FEATURE_FLAGS, getDraftStorageKey, MY_CHATBOT_PAGE_ROUTE } from "@/constants";
import { useFeatureFlag } from "@/hooks";
import { AvatarPreset, IDraftChracter } from "@/types";
import { getAvatarUrl, getCharacterCardStatusBadgeLabel, getCharacterCardVariantLabel, syncDraftCharacters } from "@/utils";

import { DraftDataType, draftIdType, ModerationDataType } from "./types";

interface ICharactersResponse {
  data: ICharacter[];
  total: number;
}

export default function DraftPage() {
  const { t } = useTranslation();
  const { hasFeatureFlag: hasVoiceAttachmentFeatureFlag } = useFeatureFlag(FEATURE_FLAGS.CHARACTER_VOICE_ATTACHMENT);
  const navigate = useNavigate();
  // using this because we only want userId not guestUserId
  const { onlyUserId: userId } = useUserInfo();
  const [draftData, setDraftData] = useState<DraftDataType>(null);
  const { jsonApiService, callApi, isLoading } = useApi<ICharactersResponse>();
  const draftKey = getDraftStorageKey(userId);
  const lsDraftData = useMemo<Record<string, IDraftChracter>>(() => JSON.parse(localStorage.getItem(draftKey) || "{}"), [draftKey]);
  const draftValues: ModerationDataType[] = useMemo(() => Object.values(lsDraftData), [lsDraftData]);

  useEffect(() => {
    if (userId) {
      const moderatedData = draftValues.filter(
        (d: ModerationDataType) => d?.moderationReport?.moderation_status === CHARACTER_MODERATION_STATUS.FIX_REQUIRED,
      );

      if (!moderatedData?.length) {
        setDraftData(lsDraftData);
      } else {
        (async () => {
          const resourceData = await callApi(
            jsonApiService.fetchResource,
            `users/characters?switch=T1&type=${CharacterType.STANDARD}`,
            null,
            {},
            API_VERSION.V2,
          );
          const data = resourceData?.data;

          if (data === undefined || data === null) {
            setDraftData(lsDraftData);
            return;
          }

          // This endpoint is called without a limit, so `data` is every character the user owns and a
          // draft missing from it belongs to a character that no longer exists.
          const modifiedData = syncDraftCharacters(moderatedData, data, lsDraftData, draftKey, true);

          setDraftData(modifiedData);
        })();
      }
    }
  }, [draftKey, draftValues, lsDraftData, userId]);

  const deleteDraft = (id: draftIdType) => {
    const updatedData = { ...draftData };
    delete (updatedData as Record<string, ICharacter>)[id];
    setDraftData(updatedData);
    localStorage.setItem(draftKey, JSON.stringify(updatedData));
    if (Object.keys(updatedData).length === 0) {
      navigate(MY_CHATBOT_PAGE_ROUTE);
    }
  };

  if (isLoading || !draftData) {
    return <Spinner />;
  }

  return (
    <>
      {!!draftData && <NavigateBackButton />}
      <PageStaticMetaTags pageId="drafts" />
      {!!draftData && (
        <Text variant={TextVariants.HEADING_5_MEDIUM}>
          <Trans i18nKey="myCreation:page.yourDrafts" />
        </Text>
      )}
      <div className="grid grid-cols-[repeat(auto-fill,minmax(160px,1fr))] gap-3 mt-lg">
        {(Object.values(draftData) as IDraftChracter[])
          .filter((draftCharacter) => !!(draftCharacter?.character_id || draftCharacter?.id))
          .map((draftCharacter: IDraftChracter, idx) => {
            const characterId = (draftCharacter?.character_id || draftCharacter?.id) as string;
            const moderationStatus = draftCharacter?.moderationReport?.moderation_status;
            const variant = moderationStatus ? CharacterCardVariant.DRAFT_ACTION_REQUIRED : CharacterCardVariant.DRAFT;
            const variantLabel = getCharacterCardVariantLabel(variant, t);
            const statusBadgeLabel = getCharacterCardStatusBadgeLabel(variant, t);

            return (
              <CharacterCard
                key={idx}
                variant={variant}
                characterInfo={{
                  characterId,
                  characterName: draftCharacter?.name || "",
                  characterCreatorUserName: draftCharacter?.creator_username || "",
                  characterTitle: draftCharacter?.title || "",
                  characterMessageNumber: "-",
                  characterTokensCount: "-",
                  characterAvatarUrl: getAvatarUrl(draftCharacter?.avatar_url || "", {
                    preset: AvatarPreset.AVATAR_256X256,
                  }) as string,
                  hasVoice: hasVoiceAttachmentFeatureFlag && !!draftCharacter?.voice_id,
                  creatorPagePath: `/creator/${draftCharacter?.creator_username}`,
                  chatPagePath: `/chatbot/${moderationStatus || draftCharacter?.isEdit ? "edit" : "create"}/${characterId}`,
                  lorebookAttached: !!draftCharacter?.has_lorebooks,
                  lorebookLabel: draftCharacter?.has_lorebooks ? t("chatbotCard:tooltip.lorebookAttached") : undefined,
                  variantLabel,
                  statusBadgeLabel,
                }}
                characterCardActionsState={{
                  [CharacterCardActions.FAVORITE]: {
                    isHidden: true,
                  },
                  [CharacterCardActions.INFO]: {
                    isHidden: true,
                  },
                }}
                dropdownMenuActions={[
                  {
                    icon: IconVariants.TRASH,
                    label: t("myCreation:card.actions.remove"),
                    onPress: () => deleteDraft(characterId),
                    className: "text-red-9",
                  },
                ]}
              />
            );
          })}
      </div>
    </>
  );
}
